import { DEFAULT_FREQUENCY, DEFAULT_RATED_POWER_BASED_ON_FOR_50, DEFAULT_RATED_POWER_BASED_ON_FOR_60, DEFAULT_VOLTAGE_FOR_50, DEFAULT_VOLTAGE_FOR_60, FILTER_PROPERTIES_MAP, FILTER_TAGS, INITIAL_FILTER, STARTING_CONSTRAINTS, TAGS } from '../constants';
import { StartingCondition } from '../enums';
import { CurveCalculationResult, DebugFilter, Filter, FilterValues, LanguageValue, Tags } from '../models';
import { round } from '.';

const getDecimalsFromScientificNotation = (value: number) => Number(value.toString().split('-')[1]);

export const countDecimals = (value: number) => Number.isInteger(value) ? 0 : value.toString().includes('-')
    ? getDecimalsFromScientificNotation(value)
    : value.toString().split('.')[1].length;

export const deepCopy = (object: any) => JSON.parse(JSON.stringify(object));

export const removeWhitespace = (text: string | undefined) => text ? text.replace(/\s+/g, '') : '';

export const removeWhitespaceAndLower = (text: string | undefined) => text ? text.replace(/\s+/g, '').toLowerCase() : '';

export const isFilter = (filter: Filter | DebugFilter): filter is Filter => 'speed' in filter;

export const isFilterReady = (filter: Filter) => (Object.keys(filter) as Array<keyof Filter>).every(x => isFilterValueValid(filter[x]));

export const isFilterValuesWrong = (filterValues: FilterValues) => (Object.keys(filterValues) as Array<keyof FilterValues>).some(x => filterValues[x].length === 0);

export const getAccordionClosedText = (...values: string[]) => values.filter(x => x).join(', ');

export const getDefaultVoltage = (frequency: number) => frequency === DEFAULT_FREQUENCY ? DEFAULT_VOLTAGE_FOR_50 : DEFAULT_VOLTAGE_FOR_60;

export const getDefaultRatedPowerBasedOn = (frequency: number) => frequency === DEFAULT_FREQUENCY ? DEFAULT_RATED_POWER_BASED_ON_FOR_50 : DEFAULT_RATED_POWER_BASED_ON_FOR_60;

export const isFilterValueValid = (value: string | number | LanguageValue | undefined | null) =>
    typeof value === 'number' || Boolean(value && typeof value !== 'object') || (typeof value === 'object' && value && value.en !== '');

export const shouldAutoFillFilterProperty = (filterValuesProperty: keyof FilterValues, filter: Partial<Filter>, filterValues: FilterValues) => {
    return filterValues[filterValuesProperty].length === 1 && !isFilterValueValid(filter[FILTER_PROPERTIES_MAP[filterValuesProperty]]);
};

export const needAutoFillFilter = (filter: Filter, filterValues: FilterValues) => (Object.keys(filterValues) as Array<keyof FilterValues>).some(x => shouldAutoFillFilterProperty(x, filter, filterValues));

export const getDefaultFilter = (frequency: number = DEFAULT_FREQUENCY): Filter => {
    return {
        ...INITIAL_FILTER,
        frequency,
        voltage: getDefaultVoltage(frequency),
        ratedPowerBasedOn: getDefaultRatedPowerBasedOn(frequency)
    };
};

const getGearboxInertiaMax = (calculationResponse: CurveCalculationResult, startingCondition: StartingCondition) =>
    calculationResponse && calculationResponse.loadOperationValues
        ? startingCondition === StartingCondition.Cold
            ? calculationResponse.loadOperationValues.gearboxInertiaMaxCold
            : calculationResponse.loadOperationValues.gearboxInertiaMaxWarm
        : null;
    
const getSubstituionDecimals = (decimalCounts: number) => Number((0).toFixed(decimalCounts).replace('.0', '.') + 1);

export const getGearboxInertiaSubtitutedMax = (calculationResponse: CurveCalculationResult, startingCondition: StartingCondition) => {
    const gearboxInertiaMax = getGearboxInertiaMax(calculationResponse, startingCondition);
    const gearboxInertiaMaxDecimalCounts = gearboxInertiaMax && countDecimals(gearboxInertiaMax);
    const gearboxInertiaSubtitutedMax = gearboxInertiaMaxDecimalCounts && (gearboxInertiaMaxDecimalCounts === 0 ? gearboxInertiaMax - 1 : gearboxInertiaMax - getSubstituionDecimals(gearboxInertiaMaxDecimalCounts));

    return round(Number(gearboxInertiaSubtitutedMax), 4);
};

export const getLoadInertiaMax = (calculationResponse: CurveCalculationResult | undefined, startingCondition: StartingCondition) =>
    (calculationResponse && calculationResponse.loadOperationValues)
        ? startingCondition === StartingCondition.Cold
            ? calculationResponse.loadOperationValues.jExternalMaxACold
            : calculationResponse.loadOperationValues.jExternalMaxAWarm
        : null;

export const getNumberOfStartupsMax = (calculationResponse: CurveCalculationResult | undefined, startingCondition: string) =>
    calculationResponse && calculationResponse.loadOperationValues
        ? startingCondition === STARTING_CONSTRAINTS.coldCondition
        ? calculationResponse.loadOperationValues.maxNumberOfACold
        : calculationResponse.loadOperationValues.maxNumberOfAWarm
    : null;

export const getN2Max = (syncSpeed: number) => syncSpeed * 10;

export const getChangedFilter = (filter: Filter, newFilter: Partial<Filter>): Filter => ({ ...filter, ...newFilter });

export const getChangedTags = (tags: Tags, newTags: Partial<Tags>): Tags => ({ ...tags, ...newTags });

export const getNonFilterTags = TAGS.filter(x => FILTER_TAGS.every(y => y !== x));

export const getDefaultMinStartingVoltage = (isStartingTypeDol: boolean) => isStartingTypeDol ? 70 : 90;

const get1LEMinStartingVoltage = (isStartingTypeDol: boolean) => isStartingTypeDol ? 80 : 90;

export const getStartingVoltageMin = (mlfb: string, isStartingTypeDol: boolean) => mlfb
    ? mlfb.startsWith('1MB')
        ? 90
        : mlfb.startsWith('1LE')
            ? get1LEMinStartingVoltage(isStartingTypeDol)
            : getDefaultMinStartingVoltage(isStartingTypeDol)
    : getDefaultMinStartingVoltage(isStartingTypeDol);
